/**
 * UpNextPreview — fixed-position rail shown on the Live host page when the
 * session was launched mid-presentation (i.e., the URL carries ?return_to=…).
 * Displays the section that follows the just-running quiz section so the
 * trainer knows what's queued for resume — without leaving host mode.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { ChevronDown, ChevronUp, Layers, ImageIcon } from 'lucide-react';

export default function UpNextPreview({ session }) {
  const { get } = useApi();
  const [moduleData, setModuleData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const moduleId = session?.target_module_id;
  const sectionId = session?.target_section_id;

  useEffect(() => {
    if (!moduleId) return;
    get(`/modules/${moduleId}`).then(setModuleData).catch(() => setModuleData(null));
  }, [moduleId, get]);

  if (!moduleId || !sectionId || !moduleData) return null;

  const sections = moduleData.sections || [];
  const currentIdx = sections.findIndex(s => s.section_id === sectionId);
  const nextSection = currentIdx >= 0 ? sections[currentIdx + 1] : null;
  const isFinal = currentIdx === sections.length - 1;

  const firstSlide = nextSection?.slides?.[0];
  const isQuizSection = nextSection?.section_type === 'quiz';

  return (
    <aside
      className="fixed top-20 right-4 z-30 w-[280px] rounded-xl border border-white/15 bg-slate-950/70 backdrop-blur-xl shadow-2xl shadow-black/40 text-slate-100 overflow-hidden"
      data-testid="up-next-preview"
    >
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--lumyn-content)] hover:bg-white/5 transition"
      >
        <span className="inline-flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5" />Up next in module
        </span>
        {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
      </button>

      {!collapsed && (
        <div className="p-3 pt-1 space-y-2">
          {isFinal ? (
            <p className="text-xs text-slate-400 italic">
              This is the last section. Resuming will land at the end of the module.
            </p>
          ) : nextSection ? (
            <>
              <p className="font-display text-sm font-semibold tracking-tight line-clamp-2">
                {nextSection.title}
              </p>
              <div className="flex items-center gap-2 text-[11px] text-slate-400">
                <span>{nextSection.section_type === 'quiz' ? 'Quiz' : 'Slides'}</span>
                <span aria-hidden>·</span>
                <span>{nextSection.total_slides || nextSection.slides?.length || 0} slide{(nextSection.total_slides || nextSection.slides?.length || 0) === 1 ? '' : 's'}</span>
                {isQuizSection && nextSection.total_questions != null && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{nextSection.total_questions} q</span>
                  </>
                )}
              </div>
              {firstSlide?.image_url ? (
                <img
                  src={firstSlide.image_url}
                  alt={firstSlide.title || 'Up next slide'}
                  className="w-full aspect-video object-cover rounded-md border border-white/10"
                  data-testid="up-next-thumb"
                />
              ) : (
                <div className="w-full aspect-video rounded-md border border-white/10 bg-white/[0.04] flex items-center justify-center text-slate-500">
                  <ImageIcon className="w-6 h-6" />
                </div>
              )}
              {firstSlide?.title && (
                <p className="text-[11px] text-slate-400 line-clamp-2">{firstSlide.title}</p>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-400 italic">Next section unavailable.</p>
          )}
        </div>
      )}
    </aside>
  );
}
